/** Markdown for compiled wiki pages: theme tokens, heading anchors that match the API TOC, in-app citation links */

import { Children, isValidElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { headingAnchor, isCitationHref } from '../../lib/wiki-citations'
import { DOCS_URL } from '../shell/nav-config'
import { API_BASE } from '../../lib/api-client'

function textOf(node: ReactNode): string {
  return Children.toArray(node).map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child)
    if (isValidElement<{ children?: ReactNode }>(child)) return textOf(child.props.children)
    return ''
  }).join('')
}

/** Assigns ids in document order with the API rule: base, then base-2, base-3, ... skipping ids already used */
function anchorFactory() {
  const used = new Set<string>()
  return (children: ReactNode) => {
    const base = headingAnchor(textOf(children))
    let anchor = base
    for (let n = 2; used.has(anchor); n++) anchor = `${base}-${n}`
    used.add(anchor)
    return anchor
  }
}

/** Relative links, and absolute ones on the app or the docs site */
export function isAppHref(href: string | undefined): boolean {
  if (!href) return false
  if (/^(\/(?![/\\])|#|\?)/.test(href)) return true
  try {
    const origin = new URL(href).origin
    return origin === window.location.origin || origin === new URL(DOCS_URL).origin
  } catch {
    return false
  }
}

/** Images that may load: relative upload paths, or the app or API origin */
export function isAppImageSrc(src: string | undefined): boolean {
  if (!src) return false
  if (/^\/(?![/\\])/.test(src)) return !src.includes('\\')
  try {
    const origin = new URL(src).origin
    return origin === window.location.origin || (API_BASE !== '' && origin === new URL(API_BASE).origin)
  } catch {
    return false
  }
}

export function WikiMarkdown({ content, isCitation = isCitationHref, untrusted = false, generated = false }: {
  content: string
  /** Which links render as citations; by default only in-app Source links, so page text cannot pose as one */
  isCitation?: (href: string) => boolean
  /**
   * Model-written text shown as-is (Ask answers): images never load (their alt text shows instead), and links that
   * leave the app or docs site are plain text with the URL visible, so injected text cannot plant a beacon or a
   * disguised phishing link
   */
  untrusted?: boolean
  /**
   * Pages an LLM wrote (server compile): whatever the stored markdown says, only relative or app/API-origin images
   * load (others show their alt text), and links that leave the app or docs site show as plain text with the URL
   */
  generated?: boolean
}) {
  const anchor = anchorFactory()
  const heading = (Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => ({ children }: { children?: ReactNode }) => (
    <Tag id={anchor(children)} className="scroll-mt-20">{children}</Tag>
  )

  return (
    <div className="prose prose-sm max-w-none md:prose-base
      prose-headings:font-display prose-headings:text-fg prose-p:text-fg-muted prose-li:text-fg-muted prose-strong:text-fg
      prose-a:text-accent-text prose-a:no-underline hover:prose-a:underline
      prose-code:text-fg prose-code:bg-surface-muted prose-code:rounded prose-code:px-1 prose-code:before:content-none prose-code:after:content-none
      prose-pre:bg-surface-muted prose-pre:text-fg prose-pre:border prose-pre:border-border
      prose-blockquote:text-fg-muted prose-blockquote:border-border-strong
      prose-th:text-fg prose-td:text-fg-muted prose-hr:border-border"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: heading('h1'),
          h2: heading('h2'),
          h3: heading('h3'),
          h4: heading('h4'),
          h5: heading('h5'),
          h6: heading('h6'),
          a: ({ href, title, children }) => {
            if (title === 'citation' && href && isCitation(href)) {
              return (
                <Link to={href} className="ml-0.5 align-super text-[0.7em] font-semibold text-accent-text no-underline">
                  {children}
                </Link>
              )
            }
            if ((untrusted || generated) && !isAppHref(href)) {
              const label = textOf(children)
              return <span className="break-all">{label}{href && href !== label ? ` (${href})` : ''}</span>
            }
            const external = href?.startsWith('http')
            return (
              <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{children}</a>
            )
          },
          ...(untrusted || generated
            ? {
              img: ({ src, alt }: { src?: string; alt?: string }) => (!untrusted && isAppImageSrc(src)
                ? <img src={src} alt={alt ?? ''} />
                : <>{alt ?? ''}</>),
            }
            : {}),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
